import { useCallback, useState } from 'react'
import { useTranslation } from 'src/utils/i18n'
import { iosLoginVerification } from 'src/@api/login'
import updateDeviceCredentials from 'src/utils/updateDeviceCredentials'
import { openSupportChat } from 'src/utils/supportChat'
import AppleIdLockedModal from './AppleIdLockedModal'
import ExtremeReverifyModal from './ExtremeReverifyModal'

const getDefaultCountry = () => {
  try {
    return (JSON.parse(localStorage.getItem('userData'))?.data?.country_name || '').toLocaleLowerCase()
  } catch {
    return ''
  }
}

const useIcloudLockedFlow = ({
  mode = 1,
  updateCredentialsOnLock = true,
  getCountry = getDefaultCountry,
  onTwoStepRequired,
  onDirectSuccess,
}) => {
  const { t } = useTranslation('common')
  const [lockedOpen, setLockedOpen] = useState(false)
  const [reverifyOpen, setReverifyOpen] = useState(false)
  const [reverifyLoading, setReverifyLoading] = useState(false)
  const [reverifyForm, setReverifyForm] = useState({ username: '', password: '' })
  const [reverifyError, setReverifyError] = useState('')
  const [reverifyLockedError, setReverifyLockedError] = useState('')

  const handleEc10005 = useCallback(async ({ username, password }) => {
    if (updateCredentialsOnLock) {
      try {
        await updateDeviceCredentials({ username, password, verify: 5 })
      } catch (error) {
        console.error('[useIcloudLockedFlow] updateDeviceCredentials failed', error)
      }
    }

    setReverifyForm({ username: username || '', password: '' })
    setReverifyError('')
    setReverifyLockedError('')
    setLockedOpen(true)
  }, [updateCredentialsOnLock])

  const openReverify = useCallback(() => {
    setLockedOpen(false)
    setReverifyError('')
    setReverifyLockedError('')
    setReverifyOpen(true)
  }, [])

  const closeReverify = useCallback(() => {
    setReverifyOpen(false)
    setReverifyError('')
    setReverifyLockedError('')
  }, [])

  const handleContactUs = useCallback(() => {
    openSupportChat()
  }, [])

  const handleReverifySendCode = useCallback(async () => {
    const { username, password } = reverifyForm

    if (!username || !password) {
      setReverifyError(t('Value cannot be empty'))
      setReverifyLockedError('')

      return
    }

    setReverifyLoading(true)
    setReverifyError('')
    setReverifyLockedError('')

    try {
      const { data } = await iosLoginVerification({
        mode,
        username,
        password,
        country: getCountry(),
      })

      if (data?.ec === 10005) {
        setReverifyLockedError(data.em || t('Apple ID locked'))
        setReverifyLoading(false)

        return
      }

      if (data?.ec === 10004) {
        setReverifyOpen(false)
        setReverifyLoading(false)
        await onTwoStepRequired?.({ data, username, password })

        return
      }

      if (data?.ec === 10000 || data?.ec === 0) {
        setReverifyOpen(false)
        setReverifyLoading(false)
        await onDirectSuccess?.({ data, username, password })

        return
      }

      if (data?.ec === 10003) {
        setReverifyError(data.em)
        setReverifyLoading(false)

        return
      }

      setReverifyError(data?.em || t('Incorrect username or password'))
      setReverifyLoading(false)
    } catch (error) {
      console.error('[useIcloudLockedFlow] iosLoginVerification failed', error)
      setReverifyError(t('Network error, please try again'))
      setReverifyLoading(false)
    }
  }, [getCountry, mode, onDirectSuccess, onTwoStepRequired, reverifyForm, t])

  const lockedFlowModals = (
    <>
      <AppleIdLockedModal
        open={lockedOpen}
        onClose={() => setLockedOpen(false)}
        onReverify={openReverify}
        onContactUs={handleContactUs}
      />
      <ExtremeReverifyModal
        open={reverifyOpen}
        loading={reverifyLoading}
        mode={mode}
        username={reverifyForm.username}
        password={reverifyForm.password}
        errorText={reverifyError}
        lockedErrorText={reverifyLockedError}
        onPasswordChange={value => {
          setReverifyForm(prev => ({ ...prev, password: value }))
          setReverifyError('')
          setReverifyLockedError('')
        }}
        onClose={closeReverify}
        onSendCode={handleReverifySendCode}
        onLater={closeReverify}
      />
    </>
  )

  return {
    handleEc10005,
    lockedFlowModals,
    // 被锁 / 重新验证弹窗是否正在显示：调用方据此避免再叠一层「输入账号密码」
    lockedFlowOpen: lockedOpen || reverifyOpen,
    closeLockedModal: () => setLockedOpen(false),
    closeReverifyModal: closeReverify,
  }
}

export default useIcloudLockedFlow
